import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Spinner } from 'react-bootstrap';
import axios from 'axios';

const fieldLabels = {
  nome: 'Nome',
  telefone: 'Telefone',
  email: 'E-mail',
  empresa: 'Empresa',
  cidade: 'Cidade',
  estado: 'Estado',
};

const badgeVariant = status => ({
  active: 'primary',
  paused: 'warning',
  completed: 'success',
  handed_off: 'warning',
  expired: 'secondary',
  restarted: 'secondary',
}[status] || 'secondary');

export default function CommercialAutomationStatusCard({ leadId, colaboradores = [] }) {
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState('');
  const [showHandoff, setShowHandoff] = useState(false);
  const [handoff, setHandoff] = useState({
    funcionario_id: '',
    reason: '',
    create_task: true,
  });

  useEffect(() => {
    let mounted = true;
    if (!leadId) return undefined;

    setLoading(true);
    axios.get(`/api/leads/${leadId}/commercial-automation`)
      .then(response => {
        if (mounted) setConversation(response.data?.conversation || null);
      })
      .catch(() => {
        if (mounted) setConversation(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => { mounted = false; };
  }, [leadId]);

  const changeStatus = async action => {
    if (action === 'restart' && !window.confirm(
      'Reiniciar a qualificação comercial? O histórico será preservado, mas o progresso atual começará novamente.'
    )) return;

    setActing(true);
    setError('');
    try {
      const url = `/api/leads/${leadId}/commercial-automation/${action}`;
      const response = action === 'restart'
        ? await axios.post(url, { confirmed: true })
        : await axios.post(url);
      setConversation(response.data?.conversation || null);
    } catch (requestError) {
      setError(
        requestError.response?.data?.message
        || 'Não foi possível alterar a automação comercial.'
      );
    } finally {
      setActing(false);
    }
  };

  const submitHandoff = async () => {
    if (!handoff.reason.trim()) {
      setError('Informe o motivo do encaminhamento.');
      return;
    }
    setActing(true);
    setError('');
    try {
      const response = await axios.post(
        `/api/leads/${leadId}/commercial-automation/handoff`,
        {
          funcionario_id: handoff.funcionario_id || null,
          reason: handoff.reason.trim(),
          create_task: handoff.create_task,
        }
      );
      setConversation(response.data?.conversation || null);
      setShowHandoff(false);
    } catch (requestError) {
      setError(
        requestError.response?.data?.message
        || 'Não foi possível encaminhar para atendimento humano.'
      );
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return <div className="text-center py-2"><Spinner size="sm" animation="border" /></div>;
  }
  if (!conversation) return null;

  const answers = Object.entries(conversation.qualification_answers || {});
  const pendingFields = conversation.pending_fields || [];

  return (
    <div style={{
      background: '#f8fafc',
      border: '1px solid #cbd5e1',
      borderRadius: 12,
      padding: 16,
    }}>
      <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
        <div>
          <strong style={{ color: '#1e293b' }}>Automação comercial</strong>
          <div className="text-muted" style={{ fontSize: 12 }}>
            Progresso da qualificação deste lead
          </div>
        </div>
        <Badge bg={badgeVariant(conversation.status)}>
          {conversation.status_label}
        </Badge>
      </div>

      <div>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}
        {conversation.interest && (
          <p className="mb-2"><strong>Interesse:</strong> {conversation.interest}</p>
        )}
        {conversation.pending_question && (
          <div className="alert alert-info py-2 mb-3" style={{ fontSize: 13 }}>
            <strong>Aguardando:</strong> {conversation.pending_question}
          </div>
        )}
        {conversation.status === 'handed_off' && (
          <Alert variant="warning" className="py-2">
            <strong>Aguardando atendimento humano</strong>
            {conversation.assigned_to?.name
              ? <div>Atendente: {conversation.assigned_to.name}</div>
              : <div>Atendente ainda não definido.</div>}
            {conversation.handoff_reason && <div>Motivo: {conversation.handoff_reason}</div>}
          </Alert>
        )}
        {answers.length > 0 && (
          <div className="mb-3">
            <strong style={{ fontSize: 13 }}>Qualificação</strong>
            {answers.map(([question, answer]) => (
              <div key={question} className="mt-2" style={{ fontSize: 12 }}>
                <div className="text-muted">{question}</div>
                <div>{String(answer)}</div>
              </div>
            ))}
          </div>
        )}
        {pendingFields.length > 0 && (
          <div style={{ fontSize: 12 }}>
            <strong>Dados pendentes:</strong>{' '}
            {pendingFields.map(field => fieldLabels[field] || field).join(', ')}
          </div>
        )}
        <div className="text-muted mt-3" style={{ fontSize: 11 }}>
          {conversation.reply_count} resposta(s) automática(s)
          {conversation.expires_at
            ? ` · expira em ${new Date(conversation.expires_at).toLocaleString('pt-BR')}`
            : ''}
        </div>
        {conversation.status === 'active' && (
          <Button className="mt-3" size="sm" variant="outline-warning"
            disabled={acting} onClick={() => changeStatus('pause')}>
            {acting ? 'Pausando...' : 'Pausar automação'}
          </Button>
        )}
        {conversation.status === 'paused' && (
          <Button className="mt-3" size="sm" variant="primary"
            disabled={acting} onClick={() => changeStatus('resume')}>
            {acting ? 'Retomando...' : 'Retomar automação'}
          </Button>
        )}
        {['active', 'paused'].includes(conversation.status) && (
          <Button className="mt-3 ms-2" size="sm" variant="outline-danger"
            disabled={acting} onClick={() => setShowHandoff(value => !value)}>
            Encaminhar para atendente
          </Button>
        )}
        <Button className="mt-3 ms-2" size="sm" variant="outline-secondary"
          disabled={acting} onClick={() => changeStatus('restart')}>
          {acting ? 'Processando...' : 'Reiniciar qualificação'}
        </Button>
        {showHandoff && (
          <div className="mt-3 p-3 bg-white border rounded">
            <label className="form-label" htmlFor="commercial-handoff-employee">Atendente</label>
            <select id="commercial-handoff-employee" className="form-select form-select-sm mb-2"
              value={handoff.funcionario_id}
              onChange={event => setHandoff(current => ({
                ...current, funcionario_id: event.target.value,
              }))}>
              <option value="">Sem atendente definido</option>
              {colaboradores.map(item => (
                <option key={item.id} value={item.id}>{item.nome}</option>
              ))}
            </select>
            <label className="form-label" htmlFor="commercial-handoff-reason">Motivo</label>
            <textarea id="commercial-handoff-reason"
              className="form-control form-control-sm mb-2" rows="2"
              value={handoff.reason}
              onChange={event => setHandoff(current => ({
                ...current, reason: event.target.value,
              }))}
              placeholder="Ex.: solicitou falar com um consultor" />
            <label className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: 12 }}>
              <input type="checkbox" checked={handoff.create_task}
                onChange={event => setHandoff(current => ({
                  ...current, create_task: event.target.checked,
                }))} />
              Criar tarefa de acompanhamento para amanhã
            </label>
            <Button size="sm" variant="danger" disabled={acting}
              onClick={submitHandoff}>
              {acting ? 'Encaminhando...' : 'Confirmar encaminhamento'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
